/**
 * The long-form paragraph behind a suggestion, fetched once and kept for the session.
 *
 * This used to live inside `ExplanationSheet`, which was the only thing that wanted it.
 * Three things want it now — the sheet, a branch card expanded on a phone, and the
 * history detail asking "why does this work" about a move already made — and three copies
 * of an abort-and-race dance is three chances to get it subtly different. So it is a hook,
 * and the sheet is one of its callers rather than its owner.
 *
 * ## Why there is a cache, and why it is a module
 *
 * Nothing cached explanations before, because nothing asked twice. Expanding a card on a
 * phone changes that: the card is opened, read, closed and reopened while deciding, and
 * paying a second (or third) round trip for a paragraph the player has already been shown
 * is both slow and rude to the token budget. The cache is keyed on the suggestion id,
 * which is a server-minted `randomUUID` — unique across requests and across sessions, so
 * there is no collision to reason about — and it is a module rather than state so that a
 * card, the sheet, and the history detail all read one copy.
 *
 * It is deliberately never evicted: an explanation is a few hundred characters, a long
 * session has a few dozen, and a page load empties it. `resetExplanationCache` exists for
 * tests, whose suggestion ids are hand-written fixtures (`s-e7`) rather than UUIDs and so
 * genuinely can collide between one test and the next.
 */

import { useEffect, useState } from 'react';
import type { BranchSuggestion } from '@hc/shared';
import type { ExplanationFetcher } from './sessionView.js';

export type ExplanationLoad =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; text: string }
  | { status: 'failed' };

const explanations = new Map<string, string>();

/**
 * The paragraph already fetched for this suggestion in this session, or `null`.
 *
 * Read by the history detail, which offers the fetch only when there is nothing to show —
 * a paragraph the player has already read should simply be there.
 */
export function cachedExplanation(suggestionId: string | null | undefined): string | null {
  if (!suggestionId) return null;
  return explanations.get(suggestionId) ?? null;
}

/** Test-only. See the note at the top of this file about fixture ids. */
export function resetExplanationCache(): void {
  explanations.clear();
}

/**
 * Fetch (or recall) the explanation for `suggestion` while `active` is true.
 *
 * Inactive is `idle`, not stale text: closing a sheet or collapsing a card takes the
 * paragraph off the screen with it. Reopening the same one is instant, from the cache.
 */
export function useExplanation(
  suggestion: BranchSuggestion | null | undefined,
  fetchExplanation: ExplanationFetcher | undefined,
  active: boolean,
): ExplanationLoad {
  const suggestionId = suggestion?.id ?? null;
  // Lazily initialised from the cache so a recalled paragraph is on screen in the first
  // frame rather than after a skeleton nobody needed to see.
  const [load, setLoad] = useState<ExplanationLoad>(() => {
    const text = active ? cachedExplanation(suggestionId) : null;
    return text === null ? { status: 'idle' } : { status: 'ready', text };
  });

  useEffect(() => {
    if (!active || !suggestion || !fetchExplanation) {
      setLoad({ status: 'idle' });
      return;
    }
    const cached = explanations.get(suggestion.id);
    if (cached !== undefined) {
      setLoad({ status: 'ready', text: cached });
      return;
    }
    const controller = new AbortController();
    let live = true;
    setLoad({ status: 'loading' });
    fetchExplanation(suggestion, controller.signal)
      .then((text) => {
        // Cached before the liveness check on purpose: a paragraph that arrived after the
        // card was collapsed is still the right paragraph, and the next tap should not
        // pay for it again.
        explanations.set(suggestion.id, text);
        if (live) setLoad({ status: 'ready', text });
      })
      .catch(() => {
        if (live) setLoad({ status: 'failed' });
      });
    return () => {
      live = false;
      controller.abort();
    };
    // `suggestionId` rather than `suggestion`: a new object identity for the same
    // suggestion (a re-render of the branch set as the rest of it streams in) must not
    // re-fetch the paragraph, and must not restart the skeleton under a card being read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, suggestionId, fetchExplanation]);

  return load;
}
